import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderOpen, BarChart3, TrendingUp, Calculator, Navigation, Shield, FileText,
  LogOut, Settings, Mail, Trash2, QrCode, StickyNote, Headphones, Menu,
  ChevronDown, ChevronRight, Search, Phone, Eye, EyeOff, Check, AlertTriangle,
  Clock, CheckCircle, XCircle, Send, Plus, X, ExternalLink, Copy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import KitPubSection from '../components/KitPubSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
  in_review: { label: 'En cours', color: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700' },
  nouveau: { label: 'Nouveau', color: 'bg-red-100 text-red-700' },
  contacte: { label: 'Contacté', color: 'bg-blue-100 text-blue-700' },
  traite: { label: 'Traité', color: 'bg-emerald-100 text-emerald-700' },
};

const NAV_CATEGORIES = [
  {
    id: 'operations', label: 'Opérations', icon: FolderOpen, color: 'text-blue-600',
    items: [
      { id: 'overview', label: "Vue d'ensemble", icon: BarChart3 },
      { id: 'projects', label: 'Dossiers', icon: FolderOpen },
    ]
  },
  {
    id: 'prospects', label: 'Prospects', icon: TrendingUp, color: 'text-purple-600',
    items: [
      { id: 'prospects_all', label: 'Tous les Prospects', icon: TrendingUp },
      { id: 'prospects_estimation', label: 'Prospects Estimation', icon: Calculator },
      { id: 'prospects_radar', label: 'Prospects Radar', icon: Navigation },
      { id: 'prospects_audit', label: 'Prospects Audit', icon: FileText },
      { id: 'prospects_conseil', label: 'Prospects Conseil', icon: Shield },
    ]
  },
  {
    id: 'notes_group', label: 'Notes', icon: StickyNote, color: 'text-yellow-600',
    items: [{ id: 'notes', label: 'Mes notes', icon: StickyNote }]
  },
  {
    id: 'communication', label: 'Communication', icon: Headphones, color: 'text-orange-600',
    items: [{ id: 'hotline', label: 'Hotline', icon: Headphones }]
  },
  {
    id: 'config', label: 'Configuration', icon: Settings, color: 'text-gray-600',
    items: [
      { id: 'profile', label: 'Mon Profil', icon: Settings },
      { id: 'kit_pub', label: 'Kit Communication', icon: QrCode },
      { id: 'emails', label: 'Emails Clients', icon: Mail },
      { id: 'estimation_coefs', label: 'Coefficients Estimation', icon: Calculator },
      { id: 'purge', label: 'Purge données', icon: Trash2 },
    ]
  },
];

function CommercialSidebar({ activeTab, onTabChange, mobileOpen, onMobileClose, primaryColor }) {
  const navigate = useNavigate();
  const [openCats, setOpenCats] = useState(() => {
    const init = {};
    NAV_CATEGORIES.forEach(cat => {
      if (cat.items.some(i => i.id === activeTab)) init[cat.id] = true;
    });
    return init;
  });

  const toggleCat = (catId) => setOpenCats(prev => ({ ...prev, [catId]: !prev[catId] }));

  const handleTabChange = (tabId) => {
    onTabChange(tabId);
    if (mobileOpen) onMobileClose();
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
            <span className="text-white text-xs font-bold">C</span>
          </div>
          <span className="text-sm font-semibold text-gray-700">Espace Commercial</span>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {NAV_CATEGORIES.map(cat => {
          const isOpen = openCats[cat.id] !== false;
          const CatIcon = cat.icon;
          return (
            <div key={cat.id}>
              <button onClick={() => toggleCat(cat.id)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-2">
                  <CatIcon className={`w-3.5 h-3.5 ${cat.color}`} />
                  {cat.label}
                </div>
                {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
              {isOpen && cat.items.map(item => {
                const ItemIcon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button key={item.id} onClick={() => handleTabChange(item.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ml-2 ${isActive ? 'text-white font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                    style={isActive ? { backgroundColor: primaryColor } : {}}>
                    <ItemIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-gray-100">
        <button onClick={() => { localStorage.removeItem('commercial_token'); localStorage.removeItem('commercial_data'); navigate('/commercial/login'); }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors">
          <LogOut className="w-4 h-4" />
          <span>Déconnexion</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop */}
      <div className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-200 h-screen sticky top-0">
        {sidebarContent}
      </div>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={onMobileClose} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl">
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
}

export default function CommercialDashboard() {
  const navigate = useNavigate();
  const [commercial, setCommercial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [stats, setStats] = useState({});
  const [projects, setProjects] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [radarProspects, setRadarProspects] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [conseils, setConseils] = useState([]);
  const [notes, setNotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState({});

  const [prospectSearch, setProspectSearch] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newNote, setNewNote] = useState({ title: '', content: '' });
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [profileForm, setProfileForm] = useState({ nom: '', prenom: '', telephone: '', contact_email: '', fonction: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [emailText, setEmailText] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const togglePass = (key) => setShowPasswords(s => ({ ...s, [key]: !s[key] }));
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('commercial_token')}` });
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purging, setPurging] = useState(false);

  const [purgePreview, setPurgePreview] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('commercial_token');
    const commData = localStorage.getItem('commercial_data');
    if (!token || !commData) { navigate('/commercial/login'); return; }
    try {
      setCommercial(JSON.parse(commData));
      fetchAllData(token);
    } catch { navigate('/commercial/login'); }
  }, [navigate]);

  const fetchAllData = async (token) => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [statsRes, projectsRes, prospectsRes, radarRes, chantiersRes, conseilsRes, notesRes, messagesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/commercial/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/commercial/projects`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/commercial/prospects`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/commercial/prospects/radar`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/commercial/chantiers`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/commercial/conseil-demands`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/commercial/notes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/commercial/hotline/messages`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/commercial/settings`, { headers }).catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data);
      setProjects(projectsRes.data);
      setProspects(prospectsRes.data);
      setRadarProspects(radarRes.data);
      setChantiers(chantiersRes.data);
      setConseils(conseilsRes.data);
      setNotes(notesRes.data);
      setMessages(messagesRes.data);
      setSettings(settingsRes.data);
      setProfileForm({
        nom: settingsRes.data.nom || '',
        prenom: settingsRes.data.prenom || '',
        telephone: settingsRes.data.telephone || '',
        contact_email: settingsRes.data.contact_email || '',
        fonction: settingsRes.data.fonction || '',
      });
      setEmailText(settingsRes.data.email_confirmation_text || '');
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const primaryColor = settings.primary_color || commercial?.primary_color || '#7C3AED';
  const partnerSubdomain = settings.partner_subdomain || commercial?.partner_subdomain || '';
  const commercialSlug = settings.slug || commercial?.slug || '';
  const publicUrl = partnerSubdomain && commercialSlug ? `${window.location.origin}/p/${partnerSubdomain}/${commercialSlug}` : '';

  const allProspects = prospects;
  const estimationProspects = allProspects.filter(p => p.source === 'estimation');
  const auditProspects = allProspects.filter(p => p.source === 'audit');
  const conseilProspects = allProspects.filter(p => p.source === 'conseil');

  const filterProspects = (list) => {
    if (!prospectSearch) return list;
    const s = prospectSearch.toLowerCase();
    return list.filter(p => (p.email || '').toLowerCase().includes(s) || (p.phone || '').includes(s) || (p.address || '').toLowerCase().includes(s));
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      await axios.post(`${API}/commercial/hotline/send`, { message: newMessage }, { headers: getHeaders() });
      setNewMessage('');
      const res = await axios.get(`${API}/commercial/hotline/messages`, { headers: getHeaders() });
      setMessages(res.data);
    } catch { toast.error('Erreur envoi message'); }
    finally { setSendingMessage(false); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/commercial/profile`, profileForm, { headers: getHeaders() });
      toast.success('Profil mis à jour !');
      const res = await axios.get(`${API}/commercial/settings`, { headers: getHeaders() });
      setSettings(res.data);
      const stored = JSON.parse(localStorage.getItem('commercial_data') || '{}');
      localStorage.setItem('commercial_data', JSON.stringify({ ...stored, full_name: res.data.full_name }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingProfile(false); }
  };

  const savePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error('Les mots de passe ne correspondent pas'); return; }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/commercial/password`, { current_password: passwordForm.current_password, new_password: passwordForm.new_password }, { headers: getHeaders() });
      toast.success('Mot de passe changé !');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingPassword(false); }
  };

  const saveEmailText = async () => {
    setSavingEmail(true);
    try {
      await axios.put(`${API}/commercial/settings/email`, { email_confirmation_text: emailText }, { headers: getHeaders() });
      toast.success('Texte email enregistré !');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingEmail(false); }
  };

  const loadPurgePreview = async () => {
    try {
      const res = await axios.post(`${API}/commercial/purge/preview`, {}, { headers: getHeaders() });
      setPurgePreview(res.data);
    } catch {}
  };

  const doPurge = async () => {
    if (purgeConfirm !== 'PURGER') { toast.error('Tapez PURGER pour confirmer'); return; }
    setPurging(true);
    try {
      await axios.post(`${API}/commercial/purge/all`, { confirm: 'PURGER' }, { headers: getHeaders() });
      toast.success('Données purgées !');
      setPurgeConfirm('');
      fetchAllData(localStorage.getItem('commercial_token'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setPurging(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center"><div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" /><p className="text-gray-600">Chargement...</p></div>
    </div>
  );

  const ProspectTable = ({ list }) => (
    <div className="overflow-x-auto">
      <div className="mb-3">
        <Input placeholder="Rechercher..." value={prospectSearch} onChange={e => setProspectSearch(e.target.value)} className="max-w-sm" />
      </div>
      <table className="w-full text-sm">
        <thead><tr className="bg-gray-50 border-b">
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
        </tr></thead>
        <tbody className="divide-y divide-gray-100">
          {filterProspects(list).map(p => (
            <tr key={p.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 font-medium text-gray-900">{p.email}</td>
              <td className="px-4 py-3 text-gray-600">{p.phone || '—'}</td>
              <td className="px-4 py-3"><Badge variant="outline" className="text-xs capitalize">{p.source || 'estimation'}</Badge></td>
              <td className="px-4 py-3 text-gray-500 text-xs">{new Date(p.created_at).toLocaleDateString('fr-FR')}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${(STATUS_CONFIG[p.status] || STATUS_CONFIG.nouveau)?.color}`}>
                  {(STATUS_CONFIG[p.status] || STATUS_CONFIG.nouveau)?.label}
                </span>
              </td>
            </tr>
          ))}
          {filterProspects(list).length === 0 && (
            <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Aucun prospect</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <style>{`
        input[type="password"]::-ms-reveal,
        input[type="password"]::-ms-clear { display: none !important; }
        input::-webkit-credentials-auto-fill-button { visibility: hidden; }
      `}</style>
      <CommercialSidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
        primaryColor={primaryColor}
      />

      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
              <Menu className="w-5 h-5" />
            </button>
            {settings.logo_url && (
              <img src={settings.logo_url} alt="logo" className="h-8 w-auto object-contain" />
            )}
            <div>
              <p className="font-semibold text-gray-900 text-sm">{settings.full_name || commercial?.full_name || 'Commercial'}</p>
              <p className="text-xs text-gray-500">{settings.fonction || 'Commercial'} — {settings.company_name || ''}</p>
            </div>
          </div>
          {publicUrl && (
            <a href={publicUrl} target="_blank" rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-700 font-medium border border-purple-200 px-3 py-1.5 rounded-lg hover:bg-purple-50 transition-colors">
              <ExternalLink className="w-3.5 h-3.5" />
              Ma page publique
            </a>
          )}
        </header>

        {/* Main content */}
        <main className="flex-1 p-4 lg:p-6 space-y-6">

          {/* Vue d'ensemble */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h1 className="text-2xl font-bold text-gray-900">Vue d'ensemble</h1>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Prospects', value: stats.total_prospects || 0, icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-50' },
                  { label: 'Dossiers', value: stats.total_projects || 0, icon: FolderOpen, color: 'text-blue-600', bg: 'bg-blue-50' },
                  { label: 'Estimation', value: stats.prospects_estimation || 0, icon: Calculator, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                  { label: 'Radar', value: stats.total_radar || 0, icon: Navigation, color: 'text-amber-600', bg: 'bg-amber-50' },
                ].map(s => (
                  <Card key={s.label}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center`}>
                        <s.icon className={`w-5 h-5 ${s.color}`} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                        <p className="text-xs text-gray-500">{s.label}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {publicUrl && (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Votre page publique</p>
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-purple-600 flex-1 truncate">{publicUrl}</span>
                      <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Lien copié !'); }}
                        className="text-gray-400 hover:text-gray-600"><Copy className="w-4 h-4" /></button>
                      <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Dossiers */}
          {activeTab === 'projects' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">Mes Dossiers</h1>
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-gray-50 border-b">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      </tr></thead>
                      <tbody className="divide-y divide-gray-100">
                        {projects.map(p => (
                          <tr key={p.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">{p.client_email || p.email || '—'}</td>
                            <td className="px-4 py-3 text-gray-600 truncate max-w-[200px]">{p.address || '—'}</td>
                            <td className="px-4 py-3"><Badge variant="outline" className="text-xs">{p.property_type || 'audit'}</Badge></td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${(STATUS_CONFIG[p.status] || STATUS_CONFIG.pending)?.color}`}>
                                {(STATUS_CONFIG[p.status] || STATUS_CONFIG.pending)?.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-400">{new Date(p.created_at).toLocaleDateString('fr-FR')}</td>
                          </tr>
                        ))}
                        {projects.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Aucun dossier</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Prospects */}
          {activeTab === 'prospects_all' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">Tous mes Prospects</h1>
              <Card><CardContent className="p-4"><ProspectTable list={allProspects} /></CardContent></Card>
            </div>
          )}
          {activeTab === 'prospects_estimation' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">Prospects Estimation</h1>
              <Card><CardContent className="p-4"><ProspectTable list={estimationProspects} /></CardContent></Card>
            </div>
          )}
          {activeTab === 'prospects_radar' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">Prospects Radar Foncier</h1>
              <Card><CardContent className="p-4"><ProspectTable list={radarProspects} /></CardContent></Card>
            </div>
          )}
          {activeTab === 'prospects_audit' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">Prospects Audit</h1>
              <Card><CardContent className="p-4"><ProspectTable list={auditProspects} /></CardContent></Card>
            </div>
          )}
          {activeTab === 'prospects_conseil' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">Prospects Conseil</h1>
              <Card><CardContent className="p-4"><ProspectTable list={conseilProspects} /></CardContent></Card>
            </div>
          )}

          {/* Notes */}
          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">Mes Notes</h1>
                <Button onClick={() => setShowNoteForm(true)} size="sm" style={{ backgroundColor: primaryColor }}>
                  <Plus className="w-4 h-4 mr-1" /> Nouvelle note
                </Button>
              </div>
              {showNoteForm && (
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <Input placeholder="Titre" value={newNote.title} onChange={e => setNewNote(n => ({ ...n, title: e.target.value }))} />
                    <Textarea placeholder="Contenu..." value={newNote.content} onChange={e => setNewNote(n => ({ ...n, content: e.target.value }))} rows={3} />
                    <div className="flex gap-2">
                      <Button size="sm" style={{ backgroundColor: primaryColor }} onClick={async () => {
                        try {
                          await axios.post(`${API}/commercial/notes`, newNote, { headers: getHeaders() });
                          toast.success('Note ajoutée !');
                          setShowNoteForm(false);
                          setNewNote({ title: '', content: '' });
                          const res = await axios.get(`${API}/commercial/notes`, { headers: getHeaders() });
                          setNotes(res.data);
                        } catch { toast.error('Erreur'); }
                      }}>Enregistrer</Button>
                      <Button size="sm" variant="outline" onClick={() => setShowNoteForm(false)}>Annuler</Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              <div className="grid gap-3">
                {notes.map(note => (
                  <Card key={note.id}>
                    <CardContent className="p-4 flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium text-gray-900">{note.title || '(sans titre)'}</p>
                        <p className="text-sm text-gray-600 mt-1">{note.content}</p>
                        <p className="text-xs text-gray-400 mt-2">{new Date(note.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <button onClick={async () => {
                        await axios.delete(`${API}/commercial/notes/${note.id}`, { headers: getHeaders() });
                        setNotes(n => n.filter(x => x.id !== note.id));
                        toast.success('Note supprimée');
                      }} className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </CardContent>
                  </Card>
                ))}
                {notes.length === 0 && <p className="text-gray-400 text-sm text-center py-8">Aucune note</p>}
              </div>
            </div>
          )}

          {/* Hotline */}
          {activeTab === 'hotline' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">Hotline Support</h1>
              <Card className="max-w-2xl">
                <CardContent className="p-0">
                  <div className="h-80 overflow-y-auto p-4 space-y-3 bg-gray-50 border-b">
                    {messages.map(m => (
                      <div key={m.id} className={`flex ${m.from === 'partner' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-xs px-3 py-2 rounded-xl text-sm ${m.from === 'partner' ? 'text-white' : 'bg-white border text-gray-700'}`}
                          style={m.from === 'partner' ? { backgroundColor: primaryColor } : {}}>
                          <p>{m.message}</p>
                          <p className="text-xs mt-1 opacity-60">{new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      </div>
                    ))}
                    {messages.length === 0 && <p className="text-center text-gray-400 text-sm py-8">Aucun message</p>}
                  </div>
                  <div className="p-4 flex gap-2">
                    <Input placeholder="Votre message..." value={newMessage} onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} />
                    <Button onClick={sendMessage} disabled={sendingMessage} style={{ backgroundColor: primaryColor }}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Kit Communication */}
          {activeTab === 'kit_pub' && (
            <KitPubSection
              partnerData={{
                ...settings,
                primary_color: primaryColor,
                subdomain: `${partnerSubdomain}/${commercialSlug}`,
                full_name: settings.full_name,
                contact_phone: settings.telephone,
                contact_email: settings.contact_email,
              }}
              token={localStorage.getItem('commercial_token')}
            />
          )}

          {/* Profil */}
          {activeTab === 'profile' && (
            <div className="space-y-6 max-w-lg">
              <h1 className="text-2xl font-bold text-gray-900">Mon Profil</h1>
              {publicUrl && (
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4">
                    <p className="text-sm font-semibold text-purple-800 mb-2">Votre page publique</p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-purple-700 flex-1 truncate">{publicUrl}</span>
                      <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Copié !'); }} className="text-purple-500 hover:text-purple-700">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              )}
              <Card>
                <CardHeader><CardTitle className="text-base">Informations personnelles</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-sm font-medium text-gray-700">Prénom</label>
                      <Input value={profileForm.prenom} onChange={e => setProfileForm(f => ({ ...f, prenom: e.target.value }))} className="mt-1" /></div>
                    <div><label className="text-sm font-medium text-gray-700">Nom</label>
                      <Input value={profileForm.nom} onChange={e => setProfileForm(f => ({ ...f, nom: e.target.value }))} className="mt-1" /></div>
                  </div>
                  <div><label className="text-sm font-medium text-gray-700">Fonction</label>
                    <Input value={profileForm.fonction} onChange={e => setProfileForm(f => ({ ...f, fonction: e.target.value }))} className="mt-1" placeholder="ex: Agent commercial, Négociateur..." /></div>
                  <div><label className="text-sm font-medium text-gray-700">Téléphone (affiché sur votre page)</label>
                    <Input value={profileForm.telephone} onChange={e => setProfileForm(f => ({ ...f, telephone: e.target.value }))} className="mt-1" placeholder="06 12 34 56 78" /></div>
                  <div><label className="text-sm font-medium text-gray-700">Email de contact (affiché sur votre page)</label>
                    <Input type="email" value={profileForm.contact_email} onChange={e => setProfileForm(f => ({ ...f, contact_email: e.target.value }))} className="mt-1" /></div>
                  <Button onClick={saveProfile} disabled={savingProfile} style={{ backgroundColor: primaryColor }} className="text-white">
                    {savingProfile ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Changer le mot de passe</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { key: 'current_password', showKey: 'current', placeholder: 'Mot de passe actuel' },
                    { key: 'new_password', showKey: 'new', placeholder: 'Nouveau mot de passe' },
                    { key: 'confirm_password', showKey: 'confirm', placeholder: 'Confirmer le nouveau' },
                  ].map(({ key, showKey, placeholder }) => (
                    <div key={key} className="relative">
                      <Input
                        type={showPasswords[showKey] ? 'text' : 'password'}
                        placeholder={placeholder}
                        value={passwordForm[key]}
                        onChange={e => setPasswordForm(f => ({ ...f, [key]: e.target.value }))}
                        className="pr-10"
                      />
                      <button type="button" onClick={() => togglePass(showKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                        {showPasswords[showKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                  <Button onClick={savePassword} disabled={savingPassword} style={{ backgroundColor: primaryColor }} className="text-white">
                    {savingPassword ? 'Enregistrement...' : 'Changer le mot de passe'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Emails */}
          {activeTab === 'emails' && (
            <div className="space-y-4 max-w-lg">
              <h1 className="text-2xl font-bold text-gray-900">Emails Clients</h1>
              <Card>
                <CardHeader><CardTitle className="text-base">Message personnalisé (email de confirmation)</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-500">Ce texte sera ajouté dans les emails envoyés à vos clients. Maximum 500 caractères.</p>
                  <Textarea value={emailText} onChange={e => setEmailText(e.target.value)} rows={5}
                    placeholder="Ex: Merci de votre confiance. Je vous recontacterai sous 24h..." maxLength={500} />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{emailText.length}/500</span>
                    <Button onClick={saveEmailText} disabled={savingEmail} style={{ backgroundColor: primaryColor }} className="text-white">
                      {savingEmail ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Coefficients */}
          {activeTab === 'estimation_coefs' && (
            <div className="space-y-4 max-w-lg">
              <h1 className="text-2xl font-bold text-gray-900">Coefficients Estimation</h1>
              <Card>
                <CardContent className="p-6 text-center text-gray-500">
                  <Calculator className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Coefficients partenaire appliqués</p>
                  <p className="text-sm mt-1">Vos estimations utilisent les coefficients configurés par votre agence. Contactez votre responsable pour les modifier.</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Purge */}
          {activeTab === 'purge' && (
            <div className="space-y-4 max-w-lg">
              <h1 className="text-2xl font-bold text-gray-900">Purge des données</h1>
              <Card className="border-red-200">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-700">Zone dangereuse</p>
                      <p className="text-sm text-gray-600 mt-1">La purge efface vos données de votre espace. <strong>Elles restent accessibles chez votre responsable d'agence.</strong></p>
                    </div>
                  </div>
                  {purgePreview.length === 0 ? (
                    <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={loadPurgePreview}>
                      Voir ce qui sera supprimé
                    </Button>
                  ) : (
                    <>
                      <div className="bg-red-50 rounded-lg p-3 space-y-1">
                        {purgePreview.map(item => (
                          <div key={item.key} className="flex justify-between text-sm">
                            <span className="text-gray-700">{item.label}</span>
                            <span className="font-semibold text-red-600">{item.count} enregistrement(s)</span>
                          </div>
                        ))}
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Tapez <strong>PURGER</strong> pour confirmer</label>
                        <Input value={purgeConfirm} onChange={e => setPurgeConfirm(e.target.value)} className="mt-1" placeholder="PURGER" />
                      </div>
                      <Button onClick={doPurge} disabled={purging || purgeConfirm !== 'PURGER'}
                        className="bg-red-600 hover:bg-red-700 text-white">
                        {purging ? 'Purge en cours...' : 'Confirmer la purge'}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
